import { type ReactNode, type Ref, useMemo } from 'react';
import {
  TextField,
  Input,
  InputGroup,
  InputGroupInput,
  InputGroupSuffix,
  Label,
  FieldError
} from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';

import { mergeRefs } from '@react-aria/utils';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FieldInputProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  /**
   * The function to format the value before displaying it.
   */
  accessor?: (value: unknown) => string | null | undefined;
  /**
   * The path inside the form object.
   */
  path: Path<T>;
  /**
   * Label text for the field.
   */
  label?: string;
  /**
   * Placeholder text.
   */
  placeholder?: string;
  /**
   * Input type.
   */
  type?: string;
  /**
   * autocomplete attribute for the underlying input. Defaults to "off"; set it
   * (e.g. "username webauthn", "current-password") on auth fields that want
   * browser autofill / passkey conditional UI.
   */
  autoComplete?: string;
  /**
   * Whether the field is required.
   */
  isRequired?: boolean;
  /**
   * Whether the field is clearable (v2 compat — not yet implemented in v3).
   */
  isClearable?: boolean;
  /**
   * Callback when the field is cleared (v2 compat).
   */
  onClear?: () => void;
  /**
   * Content to render at the end of the input (e.g. toggle icon).
   */
  endContent?: ReactNode;
  /**
   * Additional class name.
   */
  className?: string;
  [key: string]: unknown;
};

export const FieldInputBase = <T extends FieldValues = FieldValues>({
  accessor,

  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled: propDisabled,
  label,
  placeholder,
  type,
  autoComplete = 'off',
  isRequired,
  className,
  endContent,
  ref,
  ...props
}: FieldInputProps<T> & { ref?: Ref<HTMLInputElement> }) => {
  const {
    field: { disabled, value: fieldValue, ref: fieldRef, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled: propDisabled
  });

  // Memoize so the callback ref identity is stable across renders (it only
  // changes when the underlying refs do), avoiding needless detach/attach churn.
  const mergedRef = useMemo(() => mergeRefs(fieldRef, ref), [fieldRef, ref]);

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue),
    [accessor, fieldValue]
  );

  return (
    <TextField
      {...fieldProps}
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      value={value || ''}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      {endContent ? (
        <InputGroup>
          <InputGroupInput
            ref={mergedRef}
            autoComplete={autoComplete}
            placeholder={placeholder}
            type={type}
          />
          <InputGroupSuffix>{endContent}</InputGroupSuffix>
        </InputGroup>
      ) : (
        <Input
          ref={mergedRef}
          autoComplete={autoComplete}
          placeholder={placeholder}
          type={type}
        />
      )}
      <FieldError>{error?.message}</FieldError>
    </TextField>
  );
};

export const FieldInput = (props: FieldInputProps) =>
  useGlobalComponent('FieldInput', FieldInputBase, props);
